import { useState, useCallback } from "react";

/**
 * A useState wrapper that persists the value to sessionStorage.
 * When the page reloads, the state is restored from sessionStorage.
 * Useful for keeping dialogs/popovers open across iframe reloads.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKey = `tv_${key}`;

  const [state, setStateRaw] = useState<T>(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved !== null) return JSON.parse(saved);
    } catch {}
    return defaultValue;
  });

  const setState = useCallback(
    (valueOrUpdater: T | ((prev: T) => T)) => {
      setStateRaw((prev) => {
        const next =
          typeof valueOrUpdater === "function"
            ? (valueOrUpdater as (prev: T) => T)(prev)
            : valueOrUpdater;
        try {
          if (
            next === false ||
            next === null ||
            next === undefined ||
            next === ""
          ) {
            sessionStorage.removeItem(storageKey);
          } else {
            sessionStorage.setItem(storageKey, JSON.stringify(next));
          }
        } catch {}
        return next;
      });
    },
    [storageKey],
  );

  return [state, setState];
}
